import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { TrashIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DeleteWorkspaceDialog } from '@/features/workspaces/components/DeleteWorkspaceDialog';

import { SettingsRow } from './SettingsRow';

/**
 * Deleting the workspace, from its Settings (spec 198). It used to sit in a
 * menu on the home page's card, where the most destructive act in the product
 * was two clicks from the front door and beside nothing that explained it.
 * Here it is the last row of General, said plainly, for owners and org admins
 * only, and it still asks for the workspace's name before anything happens.
 *
 * Once it is gone there is nothing left to stand on, so the page leaves for the
 * workspace list, which no longer shows it.
 */
export function RemoveWorkspaceSection({
  workspaceId,
  name,
}: {
  workspaceId: string;
  name: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <SettingsRow
      id="remove-heading"
      title="Remove this workspace"
      lead="Permanent. Only its owners and organization admins can do this."
    >
      <p className="text-foreground text-sm">
        Deleting {name} takes its workflows, its tasks and their run history
        with it. Its usage and spend stay on the Usage pages under its name.
      </p>
      <div className="max-md:[&>*]:w-full">
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            setOpen(true);
          }}
        >
          <TrashIcon aria-hidden="true" />
          Delete workspace…
        </Button>
      </div>

      <DeleteWorkspaceDialog
        workspaceId={workspaceId}
        workspaceName={name}
        open={open}
        onOpenChange={setOpen}
        onDeleted={() => {
          void navigate({ to: '/' });
        }}
      />
    </SettingsRow>
  );
}
